import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Product, ProductService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { S3Service } from '../api/api_pb';
import { useGlobalContext } from '../GlobalContext';
import { useAuth } from '../AuthContext';

const EditProduct: React.FC = () => {
    const navigate = useNavigate();
    const { uuid } = useParams();
    const location = useLocation();
    const { findProductByUuid: findProductById } = useGlobalContext();
    const product = location.state?.product || findProductById(uuid!);
    const { organizationInfo } = useAuth();

    const productClient = useClient(ProductService);
    const s3Client = useClient(S3Service);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [productUsed, setProductUsed] = useState<Product>(product);


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProductUsed(prev => ({
                    ...prev,
                    imageUrl: reader.result as string
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const formatPrice = (priceInCents: number) => {
        return (priceInCents / Math.pow(10, organizationInfo?.currencyDecimalPoint ?? 0)).toFixed(organizationInfo?.currencyDecimalPoint ?? 0);
    };
    const [price, setPrice] = useState(formatPrice(parseInt(product.priceInCents)));

    const parsePrice = (formattedPrice: string) => {
        return Math.round(parseFloat(formattedPrice) * Math.pow(10, organizationInfo?.currencyDecimalPoint ?? 0));
    };

    const handleUpdate = async () => {
        if (!imageFile && !productUsed.imageUrl) return;

        setLoading(true);
        try {
            let uploadImageUrl = '';
            if (imageFile) {
                const extension = imageFile?.name.split('.').pop() || '';
                const presignedUrlResponse = await s3Client.getUploadUrl({ extension });
                const presignedUrl = presignedUrlResponse.url;

                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    body: imageFile,
                    headers: {
                        'Content-Type': imageFile!.type,
                    },
                });

                if (!uploadResponse.ok) {
                    toast.dismiss();
                    toast.error('Failed to upload image, please try again later');
                    throw new Error('Failed to upload image');
                }

                uploadImageUrl = presignedUrl.split('?')[0];
            } else {
                uploadImageUrl = productUsed.imageUrl;
            }

            await productClient.updateProduct({
                productUuid: productUsed.uuid,
                name: productUsed.name,
                imageUrl: uploadImageUrl,
                priceInCents: parsePrice(price.toString()),
                leaseDurationLimit: Math.abs(productUsed.leaseDurationLimit),
                forSale: productUsed.forSale,
            });

            toast.dismiss();
            toast.success('Product updated successfully');
            navigate('/products');
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to update product: ' + (error as Error).message);
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        setProductUsed((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    return (
        <div className="p-4">
            {loading && (
                <div className="absolute inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
                    <span className="text-white">Uploading... <div className="loader" /></span>
                </div>
            )}
            <h2 className="text-lg font-bold">Edit Product: {product.name}</h2>
            <div className="border-b border-gray-300 my-2"></div>
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Name</label>
                {/* Name */}
                <input
                    type="text"
                    name="name"
                    value={productUsed.name}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded px-2 py-1 w-full"
                />
                {/* Image Upload */}
                <label className="block text-sm font-medium text-gray-700">Image</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
                {productUsed.imageUrl && <img src={productUsed.imageUrl} alt="Preview" className="mt-2 w-32 h-32 object-contain" />}

                {/* Price with Currency Symbol */}
                <label className="block text-sm font-medium text-gray-700 mt-4">Price</label>
                <div className="flex items-center border border-gray-300 p-2 rounded w-full">
                    <span className="mr-2">{organizationInfo?.currencySymbol ?? '$'}</span>
                    <input
                        type="text"
                        name="price"
                        value={price}
                        onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, '');
                            const decimalPlaces = organizationInfo?.currencyDecimalPoint ?? 2;

                            const parts = value.split('.');
                            if (parts.length > decimalPlaces) {
                                value = parts[0] + '.' + parts.slice(1).join('');
                            }

                            if (parts.length === decimalPlaces && parts[1].length > decimalPlaces) {
                                value = parts[0] + '.' + parts[1].slice(0, decimalPlaces);
                            }
                            setPrice(value);
                        }}
                        className="flex-1 outline-none"
                        placeholder="Enter price (e.g., 12.02)"
                    />
                </div>

                {/* Lease Duration Limit */}
                <label className="block text-sm font-medium text-gray-700 mt-4">
                    Lease Duration Limit (seconds). If it's 0, the notification is disabled.
                </label>
                <input
                    type="number"
                    name="leaseDurationLimit"
                    value={productUsed.leaseDurationLimit}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded px-2 py-1 w-full"
                />
                {/* For Sale Checkbox */}
                <div className="mt-4 flex items-center">
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            name="forSale"
                            checked={productUsed.forSale}
                            onChange={handleInputChange}
                        />
                        <span className="text-sm font-medium text-gray-700">For Sale</span>
                    </label>
                </div>
            </div>
            <div className="mt-4">
                <button onClick={() => navigate(-1)} className="bg-gray-300 text-black p-2 rounded-md">Cancel</button>
                <button onClick={handleUpdate} className="bg-blue-600 text-white p-2 rounded-md ml-2">Update</button>
            </div>
        </div>
    );
};

export default EditProduct;
